<script>
  import { useForm } from '@inertiajs/svelte'
  import { liveValidate } from '../../lib/live_validation.svelte.js'
  import { dndzone, SOURCES, TRIGGERS } from 'svelte-dnd-action'
  import { flip } from 'svelte/animate'
  import Layout from '../../layouts/layout.svelte'
  import PageHeader from '../../components/page_header.svelte'
  import FormLayout from '../../components/form_layout.svelte'
  import FormSection from '../../components/form_section.svelte'
  import ScheduleEditor from '../../components/schedule_editor.svelte'
  import TextField from '../../components/form/text_field.svelte'
  import TextareaField from '../../components/form/textarea_field.svelte'
  import SelectField from '../../components/form/select_field.svelte'
  import ToggleField from '../../components/form/toggle_field.svelte'
  import SubmitButton from '../../components/form/submit_button.svelte'
  import { Button, Combobox, Icon } from '../../components/ui'

  const FLIP_DURATION_MS = 150

  let { poll, options = {}, back_url = '/polls' } = $props()

  const isEdit = !!poll.id

  // Precognitive form: liveValidate asks the real create/update action to validate each
  // changed field (`precognition_for!` before any write), so the model is the only copy of
  // the rules. Options arrive with a drag key `id` and the saved row's `option_id`; the
  // controller renumbers them in on-screen order. `return_to` brings the save back to the
  // filtered list the form was opened from.
  const form = useForm(isEdit ? 'patch' : 'post', isEdit ? `/polls/${poll.id}` : '/polls', {
    return_to: back_url,
    poll: {
      name: poll.name ?? '',
      area_id: poll.area_id ?? '',
      poll_type: poll.poll_type ?? 'question',
      display_name: poll.display_name ?? '',
      question: poll.question ?? '',
      description: poll.description ?? '',
      include_class_filter: poll.include_class_filter ?? true,
      reset_period: poll.reset_period ?? 'never',
      active: poll.active ?? true,
      poll_options: poll.poll_options ?? [],
      schedule_attributes: poll.schedule_attributes
    }
  })
  liveValidate(form, 'poll')

  // A new row's drag key only has to be unique on this page
  const addOption = () => {
    $form.poll.poll_options = [...$form.poll.poll_options, { id: crypto.randomUUID(), option_id: null, name: '', place_id: null }]
  }
  const removeOption = (id) => {
    $form.poll.poll_options = $form.poll.poll_options.filter((option) => option.id !== id)
  }
  // Options only drag from their handle — dragging anywhere else (e.g. selecting
  // text inside the name input) must not start a reorder.
  let handleHeld = $state(false)
  const startDrag = (event) => {
    event.preventDefault()
    handleHeld = true
  }
  const handleConsider = (event) => {
    $form.poll.poll_options = event.detail.items
    const { source, trigger } = event.detail.info
    if (source === SOURCES.KEYBOARD && trigger === TRIGGERS.DRAG_STOPPED) handleHeld = false
  }
  const handleFinalize = (event) => {
    $form.poll.poll_options = event.detail.items
    if (event.detail.info.source === SOURCES.POINTER) handleHeld = false
  }

  const errorFor = (key) => $form.errors[`poll.${key}`]

  const submit = () => $form.submit({ preserveScroll: true })
</script>

<Layout>
  <PageHeader title={isEdit ? 'Edit Poll' : 'New Poll'} subtitle="Daily poll rotation per college town" />

  <FormLayout
    onsubmit={(e) => {
      e.preventDefault()
      submit()
    }}
  >
    {#if errorFor('base')}<p class="text-sm text-error">{errorFor('base')}</p>{/if}

    <FormSection title="Details">
      <ToggleField bind:checked={$form.poll.active} label="Active" name="active" error={errorFor('active')} />
      <TextField bind:value={$form.poll.name} label="Name (Internal)" name="name" error={errorFor('name')} required />
      <SelectField bind:value={$form.poll.area_id} label="College Town" name="area_id" options={options.area_options ?? []} error={errorFor('area_id')} required />
      <SelectField bind:value={$form.poll.poll_type} label="Poll Type" name="poll_type" options={options.poll_type_options ?? []} error={errorFor('poll_type')} required />
    </FormSection>

    <FormSection title="Content">
      <TextField bind:value={$form.poll.display_name} label="Name" name="display_name" error={errorFor('display_name')} required />
      <TextField bind:value={$form.poll.question} label="Question" name="question" error={errorFor('question')} required />
      <TextareaField bind:value={$form.poll.description} label="Description" name="description" error={errorFor('description')} />
      <ToggleField
        bind:checked={$form.poll.include_class_filter}
        label="Include Classes Filter"
        name="include_class_filter"
        error={errorFor('include_class_filter')}
      />

      <fieldset class="fieldset">
        <legend class="fieldset-legend">Options</legend>
        <div
          use:dndzone={{ items: $form.poll.poll_options, dragDisabled: !handleHeld, flipDurationMs: FLIP_DURATION_MS, dropTargetStyle: {} }}
          onconsider={handleConsider}
          onfinalize={handleFinalize}
          class="flex flex-col gap-2"
        >
          {#each $form.poll.poll_options as option (option.id)}
            <div class="flex flex-wrap items-center gap-2 sm:flex-nowrap" animate:flip={{ duration: FLIP_DURATION_MS }}>
              <button
                type="button"
                class="cursor-grab touch-none text-base-content/30 hover:text-base-content/70"
                aria-label="Drag to reorder"
                onmousedowncapture={startDrag}
                ontouchstartcapture={startDrag}
                onmouseup={() => (handleHeld = false)}
              >
                <Icon name="drag_handle" size="sm" />
              </button>
              <input type="text" class="input input-sm min-w-0 flex-1" placeholder="Option name" bind:value={option.name} />
              <Combobox
                bind:value={option.place_id}
                source="places"
                sourceParams={{ area_id: $form.poll.area_id }}
                size="sm"
                width="w-full sm:w-56"
                clearable
                placeholder="Select place…"
                ariaLabel="Place"
              />
              <Button variant="ghost" size="xs" class="text-error" onclick={() => removeOption(option.id)}>Delete</Button>
            </div>
          {/each}
        </div>
        <Button variant="ghost" size="sm" icon="add" class="text-primary mt-2" onclick={addOption}>Add Option</Button>
        {#if errorFor('poll_options')}<p class="text-error text-sm">{errorFor('poll_options')}</p>{/if}
      </fieldset>

      <SelectField bind:value={$form.poll.reset_period} label="Reset Period" name="reset_period" options={options.reset_period_options ?? []} error={errorFor('reset_period')} required />
    </FormSection>

    <FormSection title="Schedule">
      <ScheduleEditor bind:value={$form.poll.schedule_attributes} timeZones={options.time_zones ?? []} error={errorFor('schedule_attributes')} />
    </FormSection>

    {#snippet footer()}
      <Button href={back_url} variant="ghost" size="sm">Cancel</Button>
      <SubmitButton processing={$form.processing} label="Save" full={false} />
    {/snippet}
  </FormLayout>
</Layout>
